import React, { useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody } from '../../components/Modais/styles';
import Botao from '../../components/Botoes/Botao';
import useStore from '../../services/hooks/useStore';
import { observer } from 'mobx-react-lite';
import { ParametroNome, FlexList, HeaderList, MinValorInput, MaxValorInput } from './styles';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import syncModel from '../../services/models/Sync';
import { api } from '../../services/utils';

interface ParametroValor {
  id: number;
  parametro: {
    id: number;
    nome: string;
    uuid: string;
  };
  valor_minimo: number;
  valor_maximo: number;
  used: boolean;
}

interface ModalProps {
  open: boolean;
  handleClose: () => void;
}

interface ParametroState {
  min: number;
  max: number;
  id: number;
  originalMin: number;
  originalMax: number;
  originalUsed: boolean;
  changed: boolean;
}

const ListaTodosParametros: React.FC<ModalProps> = ({ open, handleClose }) => {
  const store = useStore();
  const [parametros, setParametros] = useState<Record<string, ParametroState>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!store.parametroValor?.arr) return;

    const newParametros: Record<string, ParametroState> = {};

    Array.from(store.parametroValor.arr).forEach((parametro: ParametroValor) => {
      newParametros[parametro.parametro.uuid] = {
        min: parametro.valor_minimo,
        max: parametro.valor_maximo,
        id: parametro.parametro.id,
        originalMin: parametro.valor_minimo,
        originalMax: parametro.valor_maximo,
        originalUsed: parametro.used,
        changed: false
      };
    });

    setParametros(newParametros);
  }, [store.parametroValor]);

  const handleMinValueChange = (uuid: string, value: number) => {
    setParametros((prev) => ({
      ...prev,
      [uuid]: { 
        ...prev[uuid], 
        min: value,
        changed: value !== prev[uuid].originalMin || 
                prev[uuid].max !== prev[uuid].originalMax ||
                (store.parametroValor?.arr?.find(p => p.parametro.uuid === uuid)?.used !== prev[uuid].originalUsed)
      },
    }));
  };

  const handleMaxValueChange = (uuid: string, value: number) => {
    setParametros((prev) => ({
      ...prev,
      [uuid]: { 
        ...prev[uuid], 
        max: value,
        changed: value !== prev[uuid].originalMax ||
                prev[uuid].min !== prev[uuid].originalMin ||
                ((store.parametroValor?.arr?.find(p => p.parametro.uuid === uuid)?.used ?? prev[uuid].originalUsed) !== prev[uuid].originalUsed)
      },
    }));
  };

  const handleCheckboxChange = (parametro: ParametroValor) => {
    const newUsed = !parametro.used;
    parametro.used = newUsed;
    
    setParametros((prev) => ({
      ...prev,
      [parametro.parametro.uuid]: {
        ...prev[parametro.parametro.uuid],
        changed: newUsed !== prev[parametro.parametro.uuid].originalUsed ||
                prev[parametro.parametro.uuid].min !== prev[parametro.parametro.uuid].originalMin ||
                prev[parametro.parametro.uuid].max !== prev[parametro.parametro.uuid].originalMax
      }
    }));
  };

  const salvarParametros = async (): Promise<void> => {
    try {
      setIsLoading(true);
      store.toggleLoader();

      const parametrosArray = Array.from(store.parametroValor?.arr ?? []) as ParametroValor[];
      const atualizacoes = parametrosArray
        .filter((parametro) => {
          const estadoParametro = parametros[parametro.parametro.uuid];
          return estadoParametro?.changed === true;
        })
        .map((parametro) => {
          const estadoParametro = parametros[parametro.parametro.uuid];
        

          const data = {
            valor_maximo: estadoParametro.max,
            valor_minimo: estadoParametro.min,
            used: parametro.used,
            parametro: parametro.parametro.id,
            propriedade: store.propriedade!.id,
          };

          return api.put(
            `/parametro-valor/${parametro.id}/?propriedade=${store.propriedade!.id}`,
            data
          );
        });

      if (atualizacoes.length > 0) {
        await Promise.all(atualizacoes);
        store.notificar(`${atualizacoes.length} parâmetro(s) atualizado(s) com sucesso!`);
        await syncModel.qualidadeDaAgua();
      } else {
        store.notificar('Nenhuma alteração foi realizada.');
      }
      
      handleClose();
    } catch (error: any) {

      store.notificar(error.data.message);
    } finally {
      setIsLoading(false);
      store.toggleLoader();
    }
  };

  return (
    <Modal onClose={handleClose} open={open}>
      <ModalHeader>Selecione os parâmetros</ModalHeader>
      <ModalBody>
        <div style={{ width: '500px' }}>
          <HeaderList>
            <span>MIN</span>
            <span>MÁX</span>
          </HeaderList>
          {Array.from(store.parametroValor?.arr ?? [])
            .sort((a: ParametroValor, b: ParametroValor) =>
              a.parametro.nome.localeCompare(b.parametro.nome)
            )
            .map((parametro: ParametroValor) => (
              <FlexList 
                key={parametro.id} 
                className="parametro_list" 
                id={parametro.id.toString()}
                style={{
                  backgroundColor: parametros[parametro.parametro.uuid]?.changed ? '#f0f9ff' : 'transparent'
                }}
              >
                <ParametroNome className="labeled">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={parametro.used}
                        onChange={() => handleCheckboxChange(parametro)}
                        color="primary"
                        className="checkbox_parametro"
                        disabled={isLoading}
                      />
                    }
                    id={parametro.parametro.id.toString()}
                    label={parametro.parametro.nome}
                  />
                </ParametroNome>
                <MinValorInput
                  value={parametros[parametro.parametro.uuid]?.min ?? parametro.valor_minimo}
                  onChange={(value) => handleMinValueChange(parametro.parametro.uuid, value)}
                  precision="3"
                  className="parametro_min_input"
                  label=""
                  disabled={isLoading}
                />
                <MaxValorInput
                  value={parametros[parametro.parametro.uuid]?.max ?? parametro.valor_maximo}
                  onChange={(value) => handleMaxValueChange(parametro.parametro.uuid, value)}
                  precision="3"
                  className="parametro_max_input"
                  label=""
                  disabled={isLoading}
                />
              </FlexList>
            ))}
          <div style={{ width: '100%', display: 'flex', padding: '10px' }}>
            <Botao
              onClick={handleClose}
              variant="contained"
              cor="#FC7467"
              disabled={isLoading}
            >
              Voltar
            </Botao>
            <Botao
              onClick={salvarParametros}
              variant="contained"
              cor="#00C853"
              disabled={isLoading}
            >
              Salvar
            </Botao>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default observer(ListaTodosParametros);
